import { useLazyGetAddressFromZipcode } from "apollo/hooks/queries";
import { useEnvContext } from "context/EnvContext";
import {
  APP_PROVIDER_SEARCH,
  FEDERAL_STATES,
  MAX_PROVIDER_SEARCH_DISTANCE_KM,
  PAGINATION_COUNT_10,
  PAGINATION_RESULT_COUNTS,
  PAGINATION_START_0,
} from "core/consts";
import { ValueOf } from "core/types";
import { fromUnixTime, getUnixTime, isPast, isToday, isValid } from "date-fns";
import usePersistQueryParam from "dsl/hooks/usePersistQueryParam";
import { FILTER_QUERY_ACTIONS_TYPES } from "dsl/organisms/Filters/consts";
import { DEFAULT_PROVIDER_SEARCH_FILTER_DISTANCE } from "dsl/organisms/Filters/ProviderSearchApp/initialState";
import {
  ProviderSearchFilterActions,
  ProviderSearchFilters,
} from "dsl/organisms/Filters/ProviderSearchApp/types";
import fromFilterQueryString from "dsl/organisms/Filters/utils/fromQueryString";
import toFilterQueryString from "dsl/organisms/Filters/utils/toQueryString";
import { useEffect } from "react";
import {
  Location,
  URLSearchParamsInit,
  useLocation,
  useSearchParams,
} from "react-router-dom";

/**
 * Sync-only initializer: parses query string to state. Must not call lazy query
 * execute or setSearch so that hooks run in a stable order and no side effects run in render.
 */
function createSyncInitializer<State extends ProviderSearchFilters>(
  location: Location,
) {
  return (initialState: State): State => {
    const currentSearchString = location.search;
    if (!currentSearchString) {
      return initialState;
    }
    return fromFilterQueryString<State>(currentSearchString) ?? initialState;
  };
}

/**
 * Runs async sanitization (zipcode lookup, validation, normalisation) in useEffect
 * and updates state via dispatch so that useLazyQuery execute and setSearch never run in render.
 */
function useRunAsyncSanitization<State extends ProviderSearchFilters>({
  allowedFederalState,
  allowedZipcodes,
  setState,
  state,
}: {
  allowedFederalState: ValueOf<typeof FEDERAL_STATES>;
  allowedZipcodes: string[];
  state: State;
  setState: React.Dispatch<ProviderSearchFilterActions>;
}) {
  const getZipcode = useLazyGetAddressFromZipcode();
  const { app } = useEnvContext();
  const isProviderSearchApp = app === APP_PROVIDER_SEARCH;
  const [, setSearch] = useSearchParams();

  useEffect(() => {
    const filters = { ...state };
    const zipcodeValue = filters.zipcode?.value;

    if (zipcodeValue?.length !== 5) {
      runSyncSanitizationAndMaybeUpdate(
        filters,
        undefined,
        allowedFederalState,
        allowedZipcodes,
        isProviderSearchApp,
        setState,
        setSearch,
      );
      return;
    }

    getZipcode(zipcodeValue).then((address) => {
      if (address) {
        filters.zipcode = {
          label: `${address.zipcode}, ${address.city}, ${address.federal_state}`,
          latitude: address.latitude,
          longitude: address.longitude,
          value: address.zipcode!,
        };
      }
      runSyncSanitizationAndMaybeUpdate(
        filters,
        address,
        allowedFederalState,
        allowedZipcodes,
        isProviderSearchApp,
        setState,
        setSearch,
      );
    });
    // Intentionally run once on mount with initial parsed state; avoid running on every state change
  }, []);
}

function runSyncSanitizationAndMaybeUpdate<State extends ProviderSearchFilters>(
  filters: State,
  address: { zipcode?: string; federal_state?: string } | undefined,
  allowedFederalState: ValueOf<typeof FEDERAL_STATES>,
  allowedZipcodes: string[],
  isProviderSearchApp: boolean,
  setState: React.Dispatch<ProviderSearchFilterActions>,
  setSearch: (
    nextInit: URLSearchParamsInit,
    options?: { replace?: boolean },
  ) => void,
) {
  const shouldUpdateStartDate =
    !filters.start_date ||
    (filters.start_date &&
      (!isValid(filters.start_date) ||
        (!isToday(fromUnixTime(filters.start_date)) &&
          isPast(fromUnixTime(filters.start_date)))));

  const shouldUpdateZipcode =
    address?.federal_state !== allowedFederalState ||
    (allowedZipcodes?.length &&
      !allowedZipcodes.includes(address?.zipcode ?? ""));

  const shouldUpdateRadius =
    !filters?.distance || filters.distance > MAX_PROVIDER_SEARCH_DISTANCE_KM;

  const shouldUpdatePaginationCount =
    isProviderSearchApp &&
    (!filters?.count ||
      !PAGINATION_RESULT_COUNTS.includes(
        filters.count as (typeof PAGINATION_RESULT_COUNTS)[number],
      ));

  const shouldUpdatePaginationStart =
    isProviderSearchApp && filters?.start == null;

  if (shouldUpdatePaginationCount) {
    if (filters?.count)
      console.warn(
        `user tried to set provider search count filter to ${filters.count} in query params; resetting...`,
      );
    filters.count = PAGINATION_COUNT_10;
  }

  if (shouldUpdatePaginationStart) {
    filters.start = PAGINATION_START_0;
  }

  if (shouldUpdateStartDate) {
    filters.start_date = getUnixTime(new Date());
  }

  if (shouldUpdateRadius) {
    if (filters.distance)
      console.warn(
        `user tried to set provider search distance filter over ${MAX_PROVIDER_SEARCH_DISTANCE_KM}km in query params; resetting...`,
      );
    filters.distance = DEFAULT_PROVIDER_SEARCH_FILTER_DISTANCE;
  }

  if (shouldUpdateZipcode) {
    filters.zipcode = undefined;
    console.warn(
      `user tried to set provider search zipcode filter in ${address?.federal_state} outside of allowed federal states ${allowedFederalState} in query params; resetting...`,
    );
  }

  const needsUpdate =
    shouldUpdateRadius ||
    shouldUpdateStartDate ||
    shouldUpdateZipcode ||
    shouldUpdatePaginationCount ||
    shouldUpdatePaginationStart;

  if (needsUpdate) {
    setState({
      type: FILTER_QUERY_ACTIONS_TYPES.SET_STATE,
      value: filters,
    });
    const normalisedQueryArguments = toFilterQueryString(filters);
    setSearch(normalisedQueryArguments, { replace: true });
  }
}

export function usePersistQueryParamProviderSearch<
  State extends ProviderSearchFilters,
  Actions extends ProviderSearchFilterActions,
>(
  id: string,
  reducerFn: (state: State, action: Actions) => State,
  initialState: State,
  allowedFederalState: ValueOf<typeof FEDERAL_STATES>,
  allowedZipcodes: string[],
) {
  const location = useLocation();
  const [state, setState] = usePersistQueryParam<State, Actions>(
    id,
    reducerFn,
    initialState,
    toFilterQueryString,
    createSyncInitializer<State>(location),
  );

  useRunAsyncSanitization<State>({
    allowedFederalState,
    allowedZipcodes,
    setState: setState as React.Dispatch<ProviderSearchFilterActions>,
    state,
  });

  return [state, setState];
}
